/** A2 slice 3 — try a customer email on a real file: render (recipient, subject, body, missing fields) and send a [TEST] copy to yourself. */
import { useState } from "react";
import { Eye, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { previewCustomerEmail, testSendCustomerEmail } from "@/lib/api";
import { Field } from "./processShared";

export default function CommsTestPanel({ templates, canPublish }) {
  const [sf, setSf] = useState("");
  const [key, setKey] = useState(templates[0]?.key || "");
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const payload = () => ({ key, ...(sf.trim().toLowerCase().startsWith("sf") ? { sf_number: sf.trim() } : { case_id: sf.trim() }) });
  const err = (e) => toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message);

  const preview = async () => {
    if (!sf.trim()) return toast.error("Enter an SF number or case id");
    setBusy("preview");
    try { setResult(await previewCustomerEmail(payload())); } catch (e) { err(e); } finally { setBusy(""); }
  };
  const testSend = async () => {
    if (!sf.trim()) return toast.error("Enter an SF number or case id");
    setBusy("send");
    try { const r = await testSendCustomerEmail(payload()); toast.success(`Test copy sent to ${r.to}`); } catch (e) { err(e); } finally { setBusy(""); }
  };

  return (
    <div className="border rounded-md p-3 space-y-2 bg-slate-50/60" data-testid="comms-test-panel">
      <div className="text-xs font-medium text-slate-700">Try a template on a file</div>
      <div className="flex flex-wrap items-end gap-2">
        <Field label="SF number or case id" className="w-44"><Input value={sf} onChange={(e) => setSf(e.target.value)} placeholder="SF-12345" className="h-8 text-xs" data-testid="comms-test-sf" /></Field>
        <Field label="Template" className="w-64">
          <Select value={key} onValueChange={setKey}>
            <SelectTrigger className="h-8 text-xs" data-testid="comms-test-template"><SelectValue /></SelectTrigger>
            <SelectContent>{templates.map((t) => <SelectItem key={t.key} value={t.key} className="text-xs">{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={preview} disabled={!!busy} data-testid="comms-preview-btn">
          {busy === "preview" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Eye className="w-3.5 h-3.5 mr-1" />}Preview
        </Button>
        {canPublish && (
          <Button size="sm" className="h-8 text-xs" onClick={testSend} disabled={!!busy} title="Sends a [TEST] copy from legal@ to your own address — nothing is logged on the file" data-testid="comms-test-send-btn">
            {busy === "send" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}Send test to me
          </Button>
        )}
      </div>
      {result && (
        <div className="text-xs space-y-1 border rounded bg-white p-2" data-testid="comms-preview-result">
          <div><span className="text-slate-500">To:</span> <span data-testid="comms-preview-to">{result.to || <em className="text-rose-700">no email address on the file</em>}</span>
            <span className="text-slate-400"> · from {result.from_mailbox} · template {result.enabled ? <span className="text-emerald-700">ON</span> : <span className="text-slate-500">OFF</span>}</span></div>
          <div><span className="text-slate-500">Subject:</span> <span className="font-medium" data-testid="comms-preview-subject">{result.subject}</span></div>
          {result.missing_fields?.length > 0 && <div className="text-rose-700" data-testid="comms-preview-missing">Would NOT send — missing: {result.missing_fields.join(", ")}</div>}
          {result.already_sent && <div className="text-amber-800">Last sent {new Date(result.already_sent.at).toLocaleString("en-GB")} → {result.already_sent.to} ({result.already_sent.status})</div>}
          <pre className="whitespace-pre-wrap font-sans text-[11px] text-slate-700 max-h-64 overflow-y-auto border-t pt-1" data-testid="comms-preview-body">{result.body_text}</pre>
        </div>
      )}
    </div>
  );
}
